import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import getFormattedDuration from '@medflex/utils/getDurationFormatted';
import { useWavesurfer } from '@wavesurfer/react';
import useClassNames from '@medflex/hooks/useClassNames';
import PlaybackButtonGroup from './PlaybackButtonGroup';
import PlaybackRateChangeButton from './PlaybackRateChangeButton';

const PlayerAudioWave = forwardRef(({ className, src, ...props }, ref) => {
  const [srcUrl, setSrcUrl] = useState(null);
  const [generator] = useState(src);
  const [state, setState] = useState('idle');

  const waveContainerRef = useRef(null);

  const { wavesurfer, isPlaying } = useWavesurfer({
    container: waveContainerRef,
    height: 50,
    barWidth: 2,
    waveColor: 'rgb(191, 219, 254)',
    progressColor: 'rgb(59, 130, 246)',
    url: srcUrl,
  });

  useImperativeHandle(ref, () => wavesurfer, [wavesurfer]);

  const cn = useClassNames(
    () => [
      'flex',
      'flex-col',
      'gap-1',
      'items-start',
      'transition',
      'rounded',
      'flex-grow',
      'flex-shrink-0',
      '@container',
      'h-full',
      className,
    ],
    [className],
  );

  const next = useCallback(async () => {
    if (generator && typeof generator.next === 'function') {
      const { value: url, done } = await generator.next();
      if (!done) {
        setSrcUrl(url);
        return;
      }
    }

    setState('error');
  }, [generator]);

  useEffect(() => {
    if (state !== 'idle') return;

    if (generator && generator.next) {
      next();
      return;
    }

    setState('loading');
    setSrcUrl(generator);
  }, [generator, next, state]);

  useEffect(() => {
    if (wavesurfer) {
      wavesurfer.once('click', () => wavesurfer.play());
      wavesurfer.once('ready', () => setState('success'));
      wavesurfer.once('loading', () => setState('loading'));
      wavesurfer.once('error', () => setState('error'));
    }

    return () => {
      if (wavesurfer) {
        wavesurfer.stop();
        return wavesurfer.destroy();
      }
      return undefined;
    };
  }, [wavesurfer]);

  const handleTogglePlayback = useCallback(() => {
    if (isPlaying) {
      wavesurfer.pause();
    } else {
      wavesurfer.play();
    }
  }, [isPlaying, wavesurfer]);

  const handleWinding = useCallback(
    seconds => {
      wavesurfer.skip(seconds);
    },
    [wavesurfer],
  );

  const handlePlaybackRateChange = useCallback(
    rate => {
      wavesurfer.setPlaybackRate(rate, true);
    },
    [wavesurfer],
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div data-component="VoiceMessage__PlayerAudioWave" className={cn} {...props}>
      <div className="grid grid-rows-2 grid-cols-5 w-full items-center text-blue-500 @md:flex @md:flex-row @md:flex-1 @md:gap-4">
        {srcUrl && <div ref={waveContainerRef} className="col-span-5 @md:order-3 @md:grow" />}
        <div className="flex justify-center @md:col-span-5 @md:order-2">
          {getFormattedDuration(wavesurfer?.getCurrentTime())}
        </div>
        <div className="flex justify-center col-span-2 @md:col-span-3 @md:order-1">
          <PlaybackButtonGroup
            isPlaying={isPlaying}
            isLoading={state === 'loading'}
            hasError={state === 'error'}
            onTogglePlayback={handleTogglePlayback}
            onWinding={handleWinding}
          />
        </div>
        <div className="flex justify-center @md:col-span-1 @md:order-3">
          {wavesurfer && getFormattedDuration(wavesurfer.getDuration())}
        </div>
        <div className="flex justify-center @md:col-span-1 @md:order-3">
          <PlaybackRateChangeButton onPlaybackRateChange={handlePlaybackRateChange} />
        </div>
      </div>
    </div>
  );
});

PlayerAudioWave.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

PlayerAudioWave.defaultProps = {
  className: '',
  src: null,
};

export default PlayerAudioWave;
